import {
  createPlaybackSession as apiCreatePlaybackSession,
  controlPlaybackSession as apiControlPlaybackSession,
  getPlaybackResumeItems as apiGetPlaybackResumeItems,
  getPlaybackSession as apiGetPlaybackSession,
  getPlaybackSessions as apiGetPlaybackSessions,
  reportPlaybackClientLogs as apiReportPlaybackClientLogs,
  reportPlaybackEvent as apiReportPlaybackEvent,
  refreshPlaybackSession as apiRefreshPlaybackSession,
} from "@/api/endpoints";
import { orderedPlaybackClientLogBody, orderedPlaybackEventBody } from "@/api/playbackEventOrdering";
import { clientBuildHeaders } from "@/lib/buildInfo";
import {
  PLAYBACK_CLIENT_LOG_REQUEST_TARGET_BYTES,
  PlaybackClientLogTooLargeError,
  playbackClientLogRequestByteLength,
} from "@/pages/playbackClientLogBatching";
import type {
  CollectionResponse,
  CreatePlaybackSessionRequest,
  PaginationParams,
  PlaybackClientLogRequest,
  PlaybackEventRequest,
  PlaybackSession,
  ResumeItem,
  SingleResponse,
} from "@/types/api";

const PLAYBACK_API_BASE = "/api/v1";

type PlaybackSessionListParams = PaginationParams & { include?: string };

function playbackApiUrl(path: string): string {
  return `${PLAYBACK_API_BASE}${path}`;
}

function jsonBlob(body: unknown): Blob {
  return new Blob([JSON.stringify(body)], { type: "application/json" });
}

export function getPlaybackSession(sessionId: string, include?: string): Promise<SingleResponse<PlaybackSession>> {
  return apiGetPlaybackSession(sessionId, include);
}

export function getPlaybackSessions(
  params?: PlaybackSessionListParams,
): Promise<CollectionResponse<PlaybackSession>> {
  return apiGetPlaybackSessions(params);
}

export function getPlaybackResumeItems(
  params?: PaginationParams,
): Promise<CollectionResponse<ResumeItem>> {
  return apiGetPlaybackResumeItems(params);
}

export function createPlaybackSession(
  body: CreatePlaybackSessionRequest,
): Promise<SingleResponse<PlaybackSession>> {
  return apiCreatePlaybackSession(body);
}

export function refreshPlaybackSession(
  sessionId: string,
  body: CreatePlaybackSessionRequest,
): Promise<SingleResponse<PlaybackSession>> {
  return apiRefreshPlaybackSession(sessionId, body);
}

export function reportPlaybackEvent(
  sessionId: string,
  body: PlaybackEventRequest,
): Promise<SingleResponse<PlaybackSession>> {
  return apiReportPlaybackEvent(sessionId, body);
}

export function recoverPlaybackDelivery(
  sessionId: string,
  positionMS: number,
  failedGenerationID?: string | null,
): Promise<SingleResponse<PlaybackSession>> {
  return apiControlPlaybackSession(sessionId, {
    action: "recover_delivery",
    position_ms: Math.max(0, Math.floor(positionMS)),
    ...(failedGenerationID?.trim() ? { hls_generation_id: failedGenerationID.trim() } : {}),
  });
}

export function reportPlaybackClientLogs(
  sessionId: string,
  body: PlaybackClientLogRequest,
): Promise<SingleResponse<PlaybackSession>> {
  const byteLength = playbackClientLogRequestByteLength(body);
  if (byteLength > PLAYBACK_CLIENT_LOG_REQUEST_TARGET_BYTES) {
    return Promise.reject(
      new PlaybackClientLogTooLargeError(byteLength, PLAYBACK_CLIENT_LOG_REQUEST_TARGET_BYTES),
    );
  }
  return apiReportPlaybackClientLogs(sessionId, body);
}

export function sendPlaybackClientLogsBeacon(
  sessionId: string,
  body: PlaybackClientLogRequest,
): boolean {
  try {
    if (playbackClientLogRequestByteLength(body) > PLAYBACK_CLIENT_LOG_REQUEST_TARGET_BYTES) {
      return false;
    }
    if (typeof navigator === "undefined" || typeof navigator.sendBeacon !== "function") {
      return false;
    }
    const url = playbackApiUrl(`/playback/sessions/${encodeURIComponent(sessionId)}/client-logs`);
    return navigator.sendBeacon(url, jsonBlob(orderedPlaybackClientLogBody(body)));
  } catch {
    return false;
  }
}

export function sendPlaybackEventKeepalive(
  sessionId: string,
  body: PlaybackEventRequest,
): void {
  const orderedBody = orderedPlaybackEventBody(body);
  const url = playbackApiUrl(`/playback/sessions/${encodeURIComponent(sessionId)}/events`);
  const blob = jsonBlob(orderedBody);
  try {
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      if (navigator.sendBeacon(url, blob)) {
        return;
      }
    }
    void fetch(url, {
      method: "POST",
      credentials: "same-origin",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        ...clientBuildHeaders(),
      },
      body: JSON.stringify(orderedBody),
    }).catch(() => {});
  } catch {
    // Unload telemetry is best-effort only.
  }
}
